<template>
  <section class="relative mb-4">
    <div class="mx-2 mt-3 mb-2 rounded-lg print:hidden">
      <div class="flex mb-5 justify-center bg-white rounded-lg">
        <div
          class="px-2 pb-1 mt-3 -mb-3 mx-3 self-center text-center whitespace-normal text-2xl sm:text-4xl text-white bg-green-600/75 backdrop-blur-xs border border-white rounded-lg"
        >
          {{ I18nNO.calendarLink }}
        </div>
      </div>
    </div>
    <ClientOnly>
      <div
        class="max-sm:fixed sm:sticky z-10 max-sm:bottom-6 sm:top-[3.75rem] max-sm:w-3/4 sm:w-[unset] mx-3 sm:mt-3 sm:mb-5 p-1 flex sm:justify-around bg-green-600 sm:bg-white backdrop-blur-xs border border-blue-gray-300 rounded-lg shadow-lg print:hidden"
      >
        <div
          class="relative max-w-[50%] h-8 mr-1 sm:h-10 self-center flex items-center text-blue-gray-800 border-none rounded-lg focus:border focus:border-green-600"
        >
          <input
            v-model="filters.search"
            class="relative h-8 w-full sm:h-10 pl-7 sm:pl-8 text-sm bg-white sm:text-base truncate border border-blue-gray-300 rounded-lg hover:border-green-500 focus:border-green-600 focus:outline-hidden focus:text-base focus-within:border-green-600"
            type="search"
            placeholder="Søk..."
          />
          <span class="absolute left-1 inset-y-0 flex content-center">
            <SvgSearch class="w-5 sm:w-6" @click="focusSearch" />
          </span>
        </div>
        <DropDown
          :eventTypes="filters.eventTypes"
          :buttons="[
            {
              label: I18nNO.expandAll,
              callback: (e) => events.forEach((ev) => (ev.expanded = true)),
            },
            {
              label: I18nNO.collapseAll,
              callback: (e) => events.forEach((ev) => (ev.expanded = false)),
            },
            {
              label: I18nNO.print,
              callback: (e) => print(),
            },
          ]"
          class="flex flex-grow max-sm:max-w-1/2 sm:max-w-[45%] bg-white text-sm sm:text-base border border-blue-gray-300 focus:outline-hidden focus:border focus:border-green-600 rounded-lg"
          @toggleFilterMember="updateEventFilter"
        >
        </DropDown>
      </div>
      <TimeLine :events="events" :filters="filters" />
      <template #fallback>
        <TimeLinePlaceholder />
      </template>
    </ClientOnly>
  </section>
</template>

<script setup lang="ts">
import PlainI18nNO from "@/data/I18nNO";
const I18nNO = reactive(PlainI18nNO);
import getCalendarEvents from "@/data/CalendarEvents";
import type { Ref } from "vue";
import type EventFilters from "@/types/EventFilters";
import type { EventType } from "@/types/Events";
import { AllEventsTag } from "@/types/Events";

useHead({
  title: I18nNO.calendarLink,
});

// Filter away past events
const now = new Date();
// const oneWeekAhead = new Date().setDate(now.getDate() + 7);
// const oneMonthAhead = new Date().setMonth(now.getMonth() + 1);
const fourHoursAgo = new Date().setHours(now.getHours() - 4);
const rawEvents = reactive(
  await getCalendarEvents(useRuntimeConfig().public.apiKey)
);
const events = computed(() =>
  rawEvents.filter(
    (event) => Date.parse(event.timestamp) > fourHoursAgo // Date.parse(): millis UTC
  )
);

const filters: Ref<EventFilters> = ref({
  eventTypes: new Map<EventType, boolean>([[AllEventsTag, true]]),
  search: "",
  calendar: undefined,
  // week: false,
  // month: false,
  // calWeek: false,
  // calMonth: false,
});

const updateEventFilter = (eventTypeState: [EventType, boolean]) => {
  if (eventTypeState[0] === AllEventsTag) {
    filters.value.eventTypes.forEach((_state, type) => {
      filters.value.eventTypes.set(type, false);
    });
    filters.value.eventTypes.set(AllEventsTag, true);
  } else {
    filters.value.eventTypes.set(AllEventsTag, false);
    filters.value.eventTypes.set(
      eventTypeState[0],
      !filters.value.eventTypes.get(eventTypeState[0])
    );
  }

  let anyEventTypeFilterApplied = false;
  for (const [_type, state] of filters.value.eventTypes) {
    anyEventTypeFilterApplied = anyEventTypeFilterApplied || state;
    if (anyEventTypeFilterApplied) {
      break;
    }
  }
  if (!anyEventTypeFilterApplied) {
    filters.value.eventTypes.set(AllEventsTag, true);
  }
};

// Make filter for all eventTypes available in full Calendar
events.value.forEach((event) => {
  if (!filters.value.eventTypes.has(event.eventType)) {
    filters.value.eventTypes.set(event.eventType, false);
  }
});

// Sort eventType filters alphabetically
filters.value.eventTypes = Array.from(filters.value.eventTypes)
  .sort()
  .reduce((typeMap, [type, state]) => {
    return typeMap.set(type, state);
  }, new Map<EventType, boolean>());

const focusSearch = ({ target }: { target: HTMLElement }) => {
  (target.closest("div")?.firstChild as HTMLElement)?.focus();
};

const print = () => {
  events.value.forEach((event) => {
    event.expanded = true;
  });
  setTimeout(() => {
    window.print();
  });
  addEventListener("afterprint", () => {
    events.value.forEach((event) => {
      event.expanded = false;
    });
  });
};

onMounted(() => window.scrollTo(0, 0));
</script>
<style>
input[type="search"]::-webkit-search-cancel-button {
  appearance: none;
  cursor: pointer;
  width: 1rem;
  height: 1rem;
  margin-right: 0.2rem;
  border: 1px solid;
  border-radius: 100%;
  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48cGF0aCBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBzdHJva2Utd2lkdGg9IjMyIiBkPSJNMzY4IDM2OEwxNDQgMTQ0TTM2OCAxNDRMMTQ0IDM2OCIvPjwvc3ZnPg");
}
</style>
